import { useQueries } from "@tanstack/react-query";
import type {
  CaseRoutingView,
  DisclosureInput,
  DisclosureStatus,
  DispatchSubmissionBody,
  DocumentView,
  LenderDecisionView,
  LenderSubmissionView,
  ManualDispatchChannel,
  MoneyInput,
  MoneyView,
  OfferOptionalTerm,
  OfferStatus,
  OfferTermsInput,
  OfferView,
  PackageExportView,
  PaymentFrequency,
  ProgramVersionView,
  RecordDecisionBody,
  RecordableDecisionOutcome,
  ReconcileOutcome,
  ReviewView,
  RoutingPrepareResponse,
  RoutingPrepareResult,
  RoutingTargetView,
  SubmissionBlocker,
  SubmissionPackageView,
  SubmissionStatus,
  TexCaseView,
} from "@tex/finance-shared";
import {
  DISCLOSURE_STATUSES,
  MANUAL_DISPATCH_CHANNELS,
  MoneyError,
  OFFER_OPTIONAL_TERMS,
  PAYMENT_FREQUENCIES,
  RECONCILE_OUTCOMES,
  RECORDABLE_DECISION_OUTCOMES,
  SENT_SUBMISSION_STATUSES,
  isOfferOutcome,
  parseDecimal,
} from "@tex/finance-shared";
import { type FormEvent, type ReactNode, useState } from "react";
import { ApiError, apiGet, errorMessage } from "../lib/api";
import { useDocuments } from "../lib/documents";
import { formatAmount, formatDate, formatDay, humanize } from "../lib/format";
import {
  decisionsForSubmission,
  useAddRoutingTargets,
  useBuildPackage,
  useDecisions,
  useDispatchSubmission,
  useExportPackage,
  useOffers,
  usePackageReadiness,
  usePackages,
  usePollSubmission,
  usePrepareRouting,
  usePrepareSubmission,
  useReconcileSubmission,
  useRecordDecision,
  useRecordDisclosure,
  useRemoveRoutingTarget,
  useRouting,
  useSubmissionReadiness,
  useSubmissions,
  useWithdrawSubmission,
} from "../lib/lenders";
import { programKeys, usePartners } from "../lib/programs";
import { useReview } from "../lib/reviews";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Mono,
  Row,
  Select,
  Textarea,
} from "./ui";

/**
 * Tex "Lenders" tab: packages → submissions → decisions → offers, in the order a case moves
 * (docs/PLAN.md §4 PR 11–13). Everything shown is what the server returned; nothing here
 * computes a term, derives a rate or infers a status. Client-side gating only mirrors the
 * server's own preconditions, and every refusal the server does make is shown verbatim.
 */
export function LendersTab({
  caseId,
  view,
}: {
  caseId: string;
  view: TexCaseView;
}) {
  const review = useReview(caseId);
  const routing = useRouting(caseId);
  const packages = usePackages(caseId);
  const submissions = useSubmissions(caseId);
  const decisions = useDecisions(caseId);
  const offers = useOffers(caseId);
  const documents = useDocuments(caseId);
  const programs = useProgramIndex();
  const cleanDocuments = (documents.data?.documents ?? []).filter(
    (d) => d.availability === "available" && d.superseded_at === null,
  );
  const allDocuments = documents.data?.documents ?? [];

  return (
    <div className="space-y-6">
      <RoutingCard
        caseId={caseId}
        view={view}
        routing={routing.data ?? null}
        routingError={routing.error}
        decisions={decisions.data?.decisions ?? []}
        offers={offers.data?.offers ?? []}
        programs={programs}
      />
      <PackageCard
        caseId={caseId}
        view={view}
        review={review.data?.review ?? null}
        reviewError={review.error}
        packages={packages.data?.packages ?? []}
        packagesError={packages.error}
        programs={programs}
      />
      <SubmissionsCard
        caseId={caseId}
        packages={packages.data?.packages ?? []}
        submissions={submissions.data?.submissions ?? []}
        submissionsError={submissions.error}
        documents={allDocuments}
        cleanDocuments={cleanDocuments}
      />
      <DecisionsCard
        caseId={caseId}
        view={view}
        submissions={submissions.data?.submissions ?? []}
        decisions={decisions.data?.decisions}
        decisionsError={decisions.error}
        documents={allDocuments}
        cleanDocuments={cleanDocuments}
      />
      <OffersCard
        caseId={caseId}
        offers={offers.data}
        offersError={offers.error}
        documents={allDocuments}
        cleanDocuments={cleanDocuments}
      />
    </div>
  );
}

// ── shared bits ──

/**
 * One key per command. It rotates after a success (the next submit is a new command) and
 * after a server refusal (the server rolled back, and the operator will likely edit the body,
 * which the same key would reject as `idempotency_key_reused`). A lost response is neither:
 * the key is kept so the retry replays whatever the server may already have committed.
 */
function useIdempotencyKey() {
  const [key, setKey] = useState(() => crypto.randomUUID());
  const rotate = () => setKey(crypto.randomUUID());
  const rotateIfRefused = (err: unknown) => {
    if (err instanceof ApiError) rotate();
  };
  return { key, rotate, rotateIfRefused };
}

interface ProgramInfo {
  id: string;
  partnerId: string;
  partnerName: string;
  label: string;
  status: ProgramVersionView["status"];
}

/** Partner and program names for ids the review/package/submission views carry. */
function useProgramIndex(): {
  byId: Map<string, ProgramInfo>;
  all: ProgramInfo[];
  isLoading: boolean;
} {
  const partners = usePartners();
  const ids = partners.data?.partners.map((p) => p.id) ?? [];
  const programs = useQueries({
    queries: ids.map((id) => ({
      queryKey: programKeys.partnerPrograms(id),
      queryFn: () =>
        apiGet<{ programs: ProgramVersionView[] }>(
          `/api/v1/partners/${id}/programs`,
        ),
    })),
  });
  const partnerName = new Map(
    partners.data?.partners.map((p) => [p.id, p.display_name]) ?? [],
  );
  const all: ProgramInfo[] = [];
  for (const q of programs) {
    for (const p of q.data?.programs ?? []) {
      const name = partnerName.get(p.partner_id) ?? p.partner_id;
      all.push({
        id: p.id,
        partnerId: p.partner_id,
        partnerName: name,
        label: `${name} · ${p.title} (${p.program_code} v${p.version}, ${p.status})`,
        status: p.status,
      });
    }
  }
  return {
    byId: new Map(all.map((p) => [p.id, p])),
    all,
    isLoading: partners.isPending || programs.some((q) => q.isPending),
  };
}

const badge =
  "inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wide";

const neutralTone = "border-neutral-200 bg-neutral-100 text-neutral-800";
const amberTone = "border-amber-200 bg-amber-50 text-amber-800";
const greenTone = "border-green-200 bg-green-50 text-[var(--status-success)]";
const redTone = "border-red-200 bg-red-50 text-[var(--status-danger)]";
const blueTone = "border-primary-200 bg-primary-50 text-tex-blue";

const submissionTone: Record<SubmissionStatus, string> = {
  prepared: neutralTone,
  dispatched: amberTone,
  acknowledged: amberTone,
  decision_received: blueTone,
  delivery_failed: redTone,
  delivery_unknown: amberTone,
  withdrawn: neutralTone,
  superseded: neutralTone,
};

const offerTone: Record<OfferStatus, string> = {
  active: greenTone,
  expired: neutralTone,
  withdrawn: neutralTone,
  superseded: neutralTone,
};

const outcomeTone: Record<LenderDecisionView["outcome"], string> = {
  more_information: amberTone,
  declined: redTone,
  conditional_offer: blueTone,
  approved_offer: greenTone,
  expired: neutralTone,
  withdrawn: neutralTone,
  superseded: neutralTone,
};

function Badge({ tone, children }: { tone: string; children: ReactNode }) {
  return <span className={`${badge} ${tone}`}>{children}</span>;
}

function SyntheticBadge({ synthetic }: { synthetic: boolean }) {
  return synthetic ? <Badge tone={neutralTone}>synthetic</Badge> : null;
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-2 rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-3">
      {children}
    </div>
  );
}

function ItemBox({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-2 rounded-md border border-[var(--border-subtle)] p-3">
      {children}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-neutral-500">{children}</p>;
}

function DocumentName({
  id,
  documents,
}: {
  id: string;
  documents: DocumentView[];
}) {
  const doc = documents.find((d) => d.id === id);
  return doc ? (
    <span>
      {doc.original_filename}{" "}
      <span className="text-neutral-500">
        · {humanize(doc.document_type)} v{doc.version}
      </span>
    </span>
  ) : (
    <Mono>{id}</Mono>
  );
}

function DocumentPicker({
  label,
  hint,
  value,
  onChange,
  documents,
  required = false,
  disabled = false,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (id: string) => void;
  documents: DocumentView[];
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <Field
      label={label}
      hint={
        hint ??
        "Only documents that are uploaded, scanned clean and not superseded are listed. Upload in Documents first if it is missing."
      }
    >
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
      >
        <option value="">{required ? "Choose a document…" : "None"}</option>
        {documents.map((d) => (
          <option key={d.id} value={d.id}>
            {d.original_filename} · {humanize(d.document_type)} v{d.version}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** Wire ISO for a `datetime-local` value; the browser's zone is the operator's. */
function localToIso(local: string): string {
  return new Date(local).toISOString();
}

function nowLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** One entry per non-blank line; the operator's wording, untouched. */
function lines(raw: string): string[] {
  return raw
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Same bounds as the route schemas (`textList` in apps/api/src/routes/offers.ts).
const LIST_MAX_ENTRIES = 50;
const LIST_ENTRY_MAX_CHARS = 500;

/** `lines()` checked against the server's list bounds so the form fails before the request does. */
function boundedLines(raw: string, label: string): string[] {
  const list = lines(raw);
  if (list.length > LIST_MAX_ENTRIES)
    throw new Error(`${label}: at most ${LIST_MAX_ENTRIES} entries`);
  const long = list.find((s) => s.length > LIST_ENTRY_MAX_CHARS);
  if (long !== undefined)
    throw new Error(`${label}: each entry must be ${LIST_ENTRY_MAX_CHARS} characters or fewer`);
  return list;
}

const trimmedOrUndefined = (s: string): string | undefined =>
  s.trim() ? s.trim() : undefined;

const nonEmptyOrUndefined = (list: string[]): string[] | undefined =>
  list.length > 0 ? list : undefined;

/** Dollars typed as written → integer minor units. Blank means "not stated" and is omitted. */
function moneyInput(raw: string, label: string): MoneyInput | undefined {
  if (!raw.trim()) return undefined;
  try {
    const m = parseDecimal(raw);
    return { amount_minor: m.amountMinor, currency: "USD" };
  } catch (err) {
    if (err instanceof MoneyError)
      throw new Error(`${label}: enter a dollar amount like 12500.00`);
    throw err;
  }
}

function integerInput(raw: string, label: string): number | undefined {
  if (!raw.trim()) return undefined;
  const n = Number(raw.trim());
  if (!Number.isInteger(n)) throw new Error(`${label}: enter a whole number`);
  return n;
}

const CHANNEL_LABELS: Record<ManualDispatchChannel, string> = {
  partner_portal: "Partner portal",
  secure_email: "Secure email",
  other: "Other",
};

const FREQUENCY_LABELS: Record<PaymentFrequency, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annual: "Semi-annual",
  annual: "Annual",
  other: "Other (see rate / terms text)",
};

const OUTCOME_LABELS: Record<RecordableDecisionOutcome, string> = {
  more_information: "More information requested",
  declined: "Declined",
  conditional_offer: "Conditional offer",
  approved_offer: "Approved offer",
  expired: "Offer expired (partner said so)",
  withdrawn: "Offer withdrawn by partner",
};

const RECONCILE_LABELS: Record<ReconcileOutcome, string> = {
  acknowledged: "Partner acknowledged receipt",
  delivery_unknown: "Delivery unknown",
  delivery_failed: "Delivery failed",
};

const OPTIONAL_TERM_LABELS: Record<OfferOptionalTerm, string> = {
  down_payment: "down payment",
  payment_amount: "payment amount",
  rate: "rate",
  fees: "fees",
  residual: "residual / buyout amount",
  total_finance_charge: "total finance charge",
  guarantee_requirements: "guarantee requirements",
  prepayment_terms: "prepayment terms",
  buyout_terms: "buyout terms",
  expires_at: "expiry",
};

const DISCLOSURE_LABELS: Record<DisclosureStatus, string> = {
  pending: "Pending — provider has not yet delivered it",
  not_required: "Not required (say why)",
  provider_delivered: "Provider delivered (attach the disclosure)",
};

/** Human wording for every readiness blocker the package planner and dispatch can raise. */
function blockerLabel(b: SubmissionBlocker): string {
  switch (b.kind) {
    case "case_stage":
      return `Case is in ${humanize(b.stage)}; packages are built in review or lender review only.`;
    case "no_revision":
      return "The borrower has not submitted an application revision.";
    case "jurisdiction_blocked":
      return `${b.state} (${b.field === "borrower.state" ? "borrower" : "equipment use"} state, per the ${b.source}) requires a broker licence Tex does not hold; this file cannot be routed. Close it as out of scope.`;
    case "no_review":
      return "No Tex review has been opened on this case.";
    case "review_not_frozen":
      return "The review is not frozen. Freeze it in the Review tab first.";
    case "review_not_ready_to_package":
      return `The review status is ${humanize(b.status)}, not ready_to_package.`;
    case "review_revision_mismatch":
      return `The frozen review covers revision ${b.review_revision_id}, but the current revision is ${b.current_revision_id}. A new review is needed.`;
    case "information_request_outstanding":
      return `Information request ${b.information_request_id} is still ${humanize(b.status)}.`;
    case "program_not_found":
      return "That program version does not exist.";
    case "program_not_routed":
      return b.routed_program_version_ids.length === 0
        ? "No program is routed on this case yet. Freeze the review or add one in Routing."
        : `This program is not in the case's routing set (${b.routed_program_version_ids.length} routed). Add it in Routing first.`;
    case "command_refused":
      return `The server refused this partner's step: ${b.code}${b.reason ? ` — ${b.reason}` : ""}.`;
    case "program_unavailable":
      return `The program version is ${humanize(b.status)}; only active or test-only programs can be packaged.`;
    case "program_not_effective":
      return `The program is not effective today (from ${b.effective_from ?? "—"} to ${b.effective_to ?? "open"}).`;
    case "partner_not_active":
      return `The partner is ${humanize(b.status)}, not active.`;
    case "partner_agreement_not_effective":
      return `The partner agreement is not effective today (from ${b.agreement_effective_from ?? "—"} to ${b.agreement_effective_to ?? "open"}).`;
    case "recipient_scope_missing":
      return "No authorized recipient scope is recorded for this partner; an ops admin records what the agreement allows in Partners.";
    case "recipient_scope_unknown_field":
      return `The partner's recipient scope names an unknown application field: ${b.field}.`;
    case "consent_missing":
      return "The borrower has not consented to sharing with a funding partner.";
    case "consent_synthetic":
      return `The only share consent on file (${b.consent_event_id}) is synthetic; a live partner needs the borrower's own.`;
    case "document_missing":
      return `Required document missing: ${humanize(b.document_type)}.`;
    case "document_out_of_scope":
      return `Document type ${humanize(b.document_type)} is required by the program but not in the partner's recipient scope.`;
    case "document_not_clean":
      return `${humanize(b.document_type)} (${b.document_id}) is ${b.upload_status} / scan ${b.scan_status}; it must be uploaded and clean.`;
    case "package_stale":
      return `The package was built from revision ${b.package_revision_id}; the current revision is ${b.current_revision_id ?? "none"}. Build a new package.`;
    case "review_superseded":
      return `The package's review ${b.package_review_id} has been superseded by ${b.latest_review_id}. Build a new package.`;
    case "program_test_only_live_partner":
      return `Program ${b.program_version_id} is test-only and the partner is live; it cannot be dispatched.`;
    case "document_changed":
      return `${humanize(b.document_type)} (${b.document_id}) has changed since the package was built.`;
    case "adapter_not_available":
      return `No adapter is available for ${humanize(b.method)} submissions.`;
    case "live_submissions_disabled":
      return "Live submissions are disabled in this environment (LIVE_SUBMISSIONS_ENABLED is off).";
    case "active_submission_exists":
      return `Submission ${b.submission_id} to this partner is still ${humanize(b.status)}; withdraw it or prepare this one as its successor.`;
    default: {
      const exhaustive: never = b;
      return `Unknown blocker ${JSON.stringify(exhaustive)}`;
    }
  }
}

function BlockerList({ blockers }: { blockers: SubmissionBlocker[] }) {
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-800">
      {blockers.map((b, i) => (
        <li key={`${b.kind}-${i}`}>
          {blockerLabel(b)}{" "}
          <Mono className="text-xs text-neutral-500">{b.kind}</Mono>
        </li>
      ))}
    </ul>
  );
}

function parseBlockers(reason: string | undefined): SubmissionBlocker[] | null {
  if (!reason) return null;
  try {
    const parsed: unknown = JSON.parse(reason);
    return Array.isArray(parsed) ? (parsed as SubmissionBlocker[]) : null;
  } catch {
    return null;
  }
}

/** The server's refusal, verbatim; blocker payloads (422) are expanded into their labels. */
function ServerError({ error }: { error: unknown }) {
  if (
    error instanceof ApiError &&
    (error.code === "package_not_buildable" ||
      error.code === "submission_not_dispatchable")
  ) {
    const blockers = parseBlockers(error.reason);
    if (blockers) {
      return (
        <Alert tone="error">
          <p>
            <Mono>{error.code}</Mono> — the server refused:
          </p>
          <BlockerList blockers={blockers} />
        </Alert>
      );
    }
  }
  if (error instanceof ApiError && error.code === "package_unchanged") {
    return (
      <Alert tone="error">
        <Mono>package_unchanged</Mono> — an identical package already exists:{" "}
        <Mono>{error.reason}</Mono>. Nothing was built.
      </Alert>
    );
  }
  return <Alert tone="error">{errorMessage(error)}</Alert>;
}

// ── routing (PR 17b): one application, N partners ──

const routingOutcomeTone: Record<RoutingPrepareResult["outcome"], string> = {
  prepared: greenTone,
  already_prepared: neutralTone,
  blocked: amberTone,
};

const ROUTING_OUTCOME_LABELS: Record<RoutingPrepareResult["outcome"], string> = {
  prepared: "Prepared",
  already_prepared: "Already prepared",
  blocked: "Blocked",
};

const th =
  "px-2 py-1.5 text-left font-display text-[11px] font-bold uppercase tracking-wide text-neutral-600";
const td = "px-2 py-2 align-top text-sm";

/**
 * Who this application is being shopped to, and where it stands with each of them, side by side.
 * Routing is the server's append-only set (`case_routing_events`), seeded from the frozen review
 * and edited here without reopening it. "Prepare for N partners" is one command that builds and
 * prepares per partner; every send is still its own attested dispatch in Submissions below.
 */
function RoutingCard({
  caseId,
  view,
  routing,
  routingError,
  decisions,
  offers,
  programs,
}: {
  caseId: string;
  view: TexCaseView;
  routing: CaseRoutingView | null;
  routingError: unknown;
  decisions: LenderDecisionView[];
  offers: OfferView[];
  programs: ReturnType<typeof useProgramIndex>;
}) {
  const targets = routing?.targets ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<string[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeReason, setRemoveReason] = useState("");
  const [lastPrepare, setLastPrepare] = useState<RoutingPrepareResponse | null>(null);
  const { key, rotate, rotateIfRefused } = useIdempotencyKey();
  const prepare = usePrepareRouting(caseId, key);
  const add = useAddRoutingTargets(caseId);
  const remove = useRemoveRoutingTarget(caseId);

  const routedIds = new Set(targets.map((t) => t.program_version_id));
  const chosen = targets.filter((t) => selected.has(t.program_version_id));
  const prepareCount = chosen.length > 0 ? chosen.length : targets.length;
  const addable = programs.all.filter(
    (p) => !routedIds.has(p.id) && p.status !== "retired",
  );
  const routable = view.stage === "review" || view.stage === "lender_review";

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runPrepare = () =>
    prepare.mutate(
      chosen.length > 0
        ? { program_version_ids: chosen.map((t) => t.program_version_id) }
        : {},
      {
        onSuccess: (r) => {
          setLastPrepare(r);
          setSelected(new Set());
          rotate();
        },
        onError: rotateIfRefused,
      },
    );

  const submitAdd = (e: FormEvent) => {
    e.preventDefault();
    if (adding.length === 0) return;
    add.mutate(
      { program_version_ids: adding },
      { onSuccess: () => setAdding([]) },
    );
  };

  const submitRemove = (e: FormEvent) => {
    e.preventDefault();
    if (!removing) return;
    remove.mutate(
      { programVersionId: removing, reason: removeReason.trim() },
      {
        onSuccess: () => {
          setRemoving(null);
          setRemoveReason("");
        },
      },
    );
  };

  const latestDecision = (submissionId: string | null) =>
    submissionId ? decisionsForSubmission(decisions, submissionId)[0] ?? null : null;
  const offerFor = (submissionId: string | null) =>
    submissionId
      ? offers.find((o) => o.submission_id === submissionId && o.status === "active") ??
        offers.find((o) => o.submission_id === submissionId) ??
        null
      : null;

  return (
    <Card title="Routing">
      <div className="space-y-4">
        <p className="text-xs text-neutral-500">
          The partners this application is being shopped to. The frozen review
          seeds the set; add or remove partners here without reopening it.
          Preparing builds a package and a <Mono>prepared</Mono> submission per
          partner, each checked against that partner's own agreement. Nothing
          is sent until each submission is dispatched below.
        </p>

        {routingError ? <Alert tone="error">{errorMessage(routingError)}</Alert> : null}

        {routing && targets.length === 0 && (
          <Empty>
            No partner is routed on this case. Freezing a ready-to-package
            review adds its target and alternates; you can also add programs
            directly below.
          </Empty>
        )}

        {targets.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-[var(--border-subtle)]">
            <table className="w-full min-w-[720px] border-collapse">
              <thead className="bg-neutral-50">
                <tr>
                  <th className={th}>
                    <span className="sr-only">Select</span>
                  </th>
                  <th className={th}>Partner / program</th>
                  <th className={th}>Readiness</th>
                  <th className={th}>Package</th>
                  <th className={th}>Submission</th>
                  <th className={th}>Decision</th>
                  <th className={th}>Offer</th>
                  <th className={th}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {targets.map((t) => {
                  const decision = latestDecision(t.submission?.id ?? null);
                  const offer = offerFor(t.submission?.id ?? null);
                  return (
                    <RoutingRow
                      key={t.program_version_id}
                      target={t}
                      checked={selected.has(t.program_version_id)}
                      onToggle={() => toggle(t.program_version_id)}
                      decision={decision}
                      offer={offer}
                      onRemove={() => {
                        setRemoving(t.program_version_id);
                        setRemoveReason("");
                      }}
                      removeDisabled={!routable || t.submission !== null}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {removing && (
          <Panel>
            <form onSubmit={submitRemove} className="space-y-2">
              <Field
                label={`Remove ${targets.find((t) => t.program_version_id === removing)?.partner_display_name ?? removing} from routing`}
                hint="Recorded as a routing event with your reason. Existing packages are kept; an active submission must be withdrawn separately first."
              >
                <Textarea
                  rows={2}
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                  required
                  minLength={3}
                  maxLength={2000}
                  disabled={remove.isPending}
                />
              </Field>
              {remove.isError && <ServerError error={remove.error} />}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setRemoving(null)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={remove.isPending}>
                  {remove.isPending ? "Removing…" : "Remove from routing"}
                </Button>
              </div>
            </form>
          </Panel>
        )}

        {targets.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-neutral-500">
              {chosen.length > 0
                ? `${chosen.length} of ${targets.length} selected.`
                : `Nothing selected: prepares every routed partner (${targets.length}).`}{" "}
              Partners that already hold an active submission are left as they
              are; a blocker for one partner never stops the others.
            </p>
            <Button
              type="button"
              disabled={prepare.isPending || !routable}
              onClick={runPrepare}
            >
              {prepare.isPending
                ? "Preparing…"
                : `Prepare for ${prepareCount} partner${prepareCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        )}
        {!routable && targets.length > 0 && (
          <Alert tone="info">
            Case is in {humanize(view.stage)}; routing changes and preparation
            happen in review or lender review only.
          </Alert>
        )}
        {prepare.isError && <ServerError error={prepare.error} />}

        {lastPrepare && (
          <Panel>
            <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
              Last preparation — {lastPrepare.prepared_count} prepared,{" "}
              {lastPrepare.already_prepared_count} already prepared,{" "}
              {lastPrepare.blocked_count} blocked
            </p>
            <ul className="space-y-2">
              {lastPrepare.results.map((r) => (
                <li key={r.program_version_id} className="space-y-1 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={routingOutcomeTone[r.outcome]}>
                      {ROUTING_OUTCOME_LABELS[r.outcome]}
                    </Badge>
                    <span className="font-medium text-neutral-900">
                      {r.partner_display_name}
                    </span>
                    {r.submission_id && (
                      <span className="text-neutral-500">
                        · submission <Mono>{r.submission_id}</Mono>
                        {r.package_built ? " · package built" : r.package_id ? " · existing package reused" : ""}
                      </span>
                    )}
                  </div>
                  {r.blockers.length > 0 && <BlockerList blockers={r.blockers} />}
                </li>
              ))}
            </ul>
          </Panel>
        )}

        <form onSubmit={submitAdd} className="space-y-2">
          <Field
            label="Add partner programs"
            hint="Hold Ctrl/Cmd to pick several. Adding a program only puts it in the routing set; its package is built and checked against that partner's agreement when you prepare."
          >
            <Select
              multiple
              size={Math.min(6, Math.max(3, addable.length))}
              value={adding}
              onChange={(e) =>
                setAdding(
                  Array.from(e.target.selectedOptions, (o) => o.value),
                )
              }
              disabled={programs.isLoading || add.isPending || !routable}
            >
              {addable.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
          {add.isError && <ServerError error={add.error} />}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="secondary"
              disabled={adding.length === 0 || add.isPending || !routable}
            >
              {add.isPending
                ? "Adding…"
                : `Add ${adding.length || ""} program${adding.length === 1 ? "" : "s"} to routing`}
            </Button>
          </div>
        </form>

        {routing && routing.history.length > 0 && (
          <details className="text-xs text-neutral-600">
            <summary className="cursor-pointer">
              Routing history ({routing.history.length} event
              {routing.history.length === 1 ? "" : "s"})
            </summary>
            <ul className="mt-1 space-y-1">
              {routing.history.map((h) => (
                <li key={h.id}>
                  <Mono>#{h.sequence}</Mono> {formatDate(h.recorded_at)} ·{" "}
                  {h.action === "added" ? "added" : "removed"}{" "}
                  <span className="font-medium text-neutral-800">
                    {h.partner_display_name}
                  </span>{" "}
                  <span className="text-neutral-500">
                    ({programs.byId.get(h.program_version_id)?.label ?? h.program_version_id})
                  </span>{" "}
                  · {h.source === "review_freeze" ? "from review freeze" : "by operator"}
                  {h.reason ? ` — ${h.reason}` : ""}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Card>
  );
}

function RoutingRow({
  target: t,
  checked,
  onToggle,
  decision,
  offer,
  onRemove,
  removeDisabled,
}: {
  target: RoutingTargetView;
  checked: boolean;
  onToggle: () => void;
  decision: LenderDecisionView | null;
  offer: OfferView | null;
  onRemove: () => void;
  removeDisabled: boolean;
}) {
  const [showBlockers, setShowBlockers] = useState(false);
  return (
    <>
      <tr className="border-t border-[var(--border-subtle)]">
        <td className={td}>
          <input
            type="checkbox"
            aria-label={`Select ${t.partner_display_name}`}
            checked={checked}
            onChange={onToggle}
          />
        </td>
        <td className={td}>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-neutral-900">
              {t.partner_display_name}
            </span>
            <SyntheticBadge synthetic={t.partner_synthetic} />
          </div>
          <div className="text-xs text-neutral-500">
            {t.program_title} · <Mono>{t.program_code}</Mono> v{t.program_version}{" "}
            · {humanize(t.program_status)}
          </div>
          <div className="text-xs text-neutral-500">
            {t.source === "review_freeze" ? "from review freeze" : "added by operator"}{" "}
            {formatDate(t.added_at)}
          </div>
        </td>
        <td className={td}>
          {t.blockers.length === 0 ? (
            <Badge tone={greenTone}>ready</Badge>
          ) : (
            <button
              type="button"
              className="text-left"
              onClick={() => setShowBlockers((v) => !v)}
            >
              <Badge tone={amberTone}>
                {t.blockers.length} blocker{t.blockers.length === 1 ? "" : "s"}
              </Badge>
            </button>
          )}
        </td>
        <td className={td}>
          {t.package ? (
            <div className="space-y-0.5">
              <Badge tone={t.package.current ? neutralTone : amberTone}>
                {t.package.current ? "built" : "stale"}
              </Badge>
              <div className="text-xs text-neutral-500">
                {formatDate(t.package.built_at)}
              </div>
            </div>
          ) : (
            <span className="text-xs text-neutral-500">none</span>
          )}
        </td>
        <td className={td}>
          {t.submission ? (
            <div className="space-y-0.5">
              <Badge tone={submissionTone[t.submission.status]}>
                {humanize(t.submission.status)}
              </Badge>
              <div className="text-xs text-neutral-500">
                {humanize(t.submission.method)}
                {t.submission.dispatched_at
                  ? ` · sent ${formatDate(t.submission.dispatched_at)}`
                  : " · not sent"}
              </div>
            </div>
          ) : (
            <span className="text-xs text-neutral-500">none</span>
          )}
        </td>
        <td className={td}>
          {decision ? (
            <Badge tone={outcomeTone[decision.outcome]}>
              {humanize(decision.outcome)}
            </Badge>
          ) : (
            <span className="text-xs text-neutral-500">—</span>
          )}
        </td>
        <td className={td}>
          {offer ? (
            <div className="space-y-0.5">
              <Badge tone={offerTone[offer.status]}>
                {humanize(offer.status)}
                {offer.selected ? " · selected" : ""}
              </Badge>
              <div className="text-xs text-neutral-500">
                {formatAmount(offer.terms.approved_amount)} · {offer.terms.term_months} mo
                {" · "}
                {offer.terms.product_type}
              </div>
            </div>
          ) : (
            <span className="text-xs text-neutral-500">—</span>
          )}
        </td>
        <td className={`${td} text-right`}>
          <Button
            type="button"
            variant="secondary"
            disabled={removeDisabled}
            onClick={onRemove}
          >
            Remove
          </Button>
        </td>
      </tr>
      {showBlockers && t.blockers.length > 0 && (
        <tr className="bg-neutral-50">
          <td />
          <td colSpan={7} className="px-2 pb-3">
            <BlockerList blockers={t.blockers} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── package (PR 11) ──

function PackageCard({
  caseId,
  view,
  review,
  reviewError,
  packages,
  packagesError,
  programs,
}: {
  caseId: string;
  view: TexCaseView;
  review: ReviewView | null;
  reviewError: unknown;
  packages: SubmissionPackageView[];
  packagesError: unknown;
  programs: ReturnType<typeof useProgramIndex>;
}) {
  const target = review?.target_program_version_id ?? null;
  const alternates = review?.alternate_program_version_ids ?? [];
  const [chosen, setChosen] = useState<string | null>(null);
  const programId = chosen ?? target ?? alternates[0] ?? null;
  const readiness = usePackageReadiness(caseId, programId);
  const { key, rotate, rotateIfRefused } = useIdempotencyKey();
  const build = useBuildPackage(caseId, key);

  const labelFor = (id: string) => programs.byId.get(id)?.label ?? id;
  const others = programs.all.filter(
    (p) => p.id !== target && !alternates.includes(p.id),
  );

  return (
    <Card title="Package">
      <div className="space-y-4">
        <p className="text-xs text-neutral-500">
          A package is the frozen review's revision and clean documents,
          filtered to what the partner's agreement allows, with a manifest
          hash. Building one does not move the case or contact anyone.
        </p>

        {reviewError ? <Alert tone="error">{errorMessage(reviewError)}</Alert> : null}
        <dl>
          <Row
            label="Frozen review"
            value={
              review
                ? review.frozen_at
                  ? `v${review.version} · frozen ${formatDate(review.frozen_at)} · ${humanize(review.status)}`
                  : `v${review.version} · not frozen · ${humanize(review.status)}`
                : "none opened"
            }
          />
          <Row label="Target program" value={target ? labelFor(target) : null} />
          <Row
            label="Alternates"
            value={
              alternates.length
                ? alternates.map((id) => labelFor(id)).join("; ")
                : null
            }
          />
          <Row label="Case stage" value={humanize(view.stage)} />
        </dl>

        <Field
          label="Program to package for"
          hint="Builds one package by hand; Routing above does this for every partner at once. The frozen review's target and alternates are listed first. Anything not in the routing set is refused by the server as program_not_routed; it is listed so the refusal is visible rather than hidden."
        >
          <Select
            value={programId ?? ""}
            onChange={(e) => setChosen(e.target.value || null)}
            disabled={programs.isLoading}
          >
            {programId === null && <option value="">Choose a program…</option>}
            {target && (
              <option value={target}>{labelFor(target)} — target</option>
            )}
            {alternates.map((id) => (
              <option key={id} value={id}>
                {labelFor(id)} — alternate
              </option>
            ))}
            {others.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>

        {programId && (
          <Panel>
            <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
              Readiness
            </p>
            {readiness.isPending && <Empty>Checking…</Empty>}
            {readiness.isError && <ServerError error={readiness.error} />}
            {readiness.data && readiness.data.blockers.length > 0 && (
              <>
                <p className="text-sm text-neutral-800">
                  {readiness.data.blockers.length} blocker
                  {readiness.data.blockers.length === 1 ? "" : "s"} — the
                  server will refuse a build until these clear.
                </p>
                <BlockerList blockers={readiness.data.blockers} />
              </>
            )}
            {readiness.data && readiness.data.blockers.length === 0 && (
              <Alert tone="success">
                Ready to build: {readiness.data.application_fields.length}{" "}
                application field
                {readiness.data.application_fields.length === 1 ? "" : "s"} and{" "}
                {readiness.data.documents.length} document
                {readiness.data.documents.length === 1 ? "" : "s"} would be
                included.
              </Alert>
            )}
            {readiness.data && (
              <details className="text-xs text-neutral-600">
                <summary className="cursor-pointer">
                  What a build would include
                </summary>
                <p className="mt-1">
                  Fields:{" "}
                  {readiness.data.application_fields.length
                    ? readiness.data.application_fields.join(", ")
                    : "none"}
                </p>
                <p className="mt-1">
                  Documents:{" "}
                  {readiness.data.documents.length
                    ? readiness.data.documents
                        .map((d) => `${humanize(d.document_type)} v${d.version}`)
                        .join(", ")
                    : "none"}
                </p>
              </details>
            )}
            {build.isError && <ServerError error={build.error} />}
            <div className="flex justify-end">
              <Button
                type="button"
                disabled={build.isPending}
                onClick={() =>
                  build.mutate(programId, {
                    onSuccess: rotate,
                    onError: rotateIfRefused,
                  })
                }
              >
                {build.isPending ? "Building…" : "Build package"}
              </Button>
            </div>
          </Panel>
        )}

        <div className="space-y-3">
          <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
            Built packages
          </p>
          {packagesError ? <Alert tone="error">{errorMessage(packagesError)}</Alert> : null}
          {packages.length === 0 && <Empty>No package has been built on this case.</Empty>}
          {packages.map((p) => (
            <PackageItem key={p.id} caseId={caseId} pkg={p} />
          ))}
        </div>
      </div>
    </Card>
  );
}

function PackageItem({
  caseId,
  pkg,
}: {
  caseId: string;
  pkg: SubmissionPackageView;
}) {
  const [exporting, setExporting] = useState(false);
  const [reason, setReason] = useState("");
  const [exported, setExported] = useState<PackageExportView | null>(null);
  const exportPackage = useExportPackage(caseId);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    exportPackage.mutate(
      { packageId: pkg.id, reason: reason.trim() },
      {
        onSuccess: (v) => {
          setExported(v);
          setExporting(false);
          setReason("");
        },
      },
    );
  };

  return (
    <ItemBox>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium text-neutral-900">
            {pkg.partner_display_name}
          </span>{" "}
          <span className="text-neutral-500">
            · {pkg.program_code} v{pkg.program_version} · built{" "}
            {formatDate(pkg.built_at)}
          </span>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setExporting((v) => !v)}
        >
          {exporting ? "Cancel" : "Export"}
        </Button>
      </div>
      <dl>
        <Row label="Package id" value={<Mono>{pkg.id}</Mono>} />
        <Row
          label="Manifest SHA-256"
          value={<Mono className="break-all text-xs">{pkg.manifest_sha256}</Mono>}
        />
        <Row
          label="Application fields shared"
          value={pkg.manifest.application_fields.length}
        />
        <Row
          label="Review / revision"
          value={`review v${pkg.manifest.review_version} · revision ${pkg.manifest.revision_number}`}
        />
        <Row label="Internal notes included" value="no" />
      </dl>
      <div>
        <p className="text-xs font-medium text-neutral-700">
          Documents ({pkg.manifest.documents.length})
        </p>
        {pkg.manifest.documents.length === 0 ? (
          <Empty>None.</Empty>
        ) : (
          <ul className="mt-1 space-y-1 text-xs">
            {pkg.manifest.documents.map((d) => (
              <li key={d.document_id} className="flex flex-wrap justify-between gap-2">
                <span>
                  {d.original_filename}{" "}
                  <span className="text-neutral-500">
                    · {humanize(d.document_type)} v{d.version}
                  </span>
                </span>
                <Mono className="break-all text-neutral-500">{d.sha256}</Mono>
              </li>
            ))}
          </ul>
        )}
      </div>

      {exporting && (
        <form onSubmit={submit} className="space-y-2">
          <Field
            label="Reason for export"
            hint="Recorded in the audit log with your user id. Exporting is not dispatching: nothing is sent to the partner, and the links below expire."
          >
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              minLength={3}
              maxLength={1000}
              disabled={exportPackage.isPending}
            />
          </Field>
          {exportPackage.isError && <ServerError error={exportPackage.error} />}
          <div className="flex justify-end">
            <Button type="submit" disabled={exportPackage.isPending}>
              {exportPackage.isPending ? "Exporting…" : "Export package"}
            </Button>
          </div>
        </form>
      )}

      {exported && (
        <Alert tone="info">
          <p className="text-sm">
            Exported {formatDate(exported.exported_at)} — reason: {exported.reason}
          </p>
          <ul className="mt-2 space-y-1 text-xs">
            {exported.documents.map((d) => (
              <li key={d.document_id}>
                {d.url ? (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-tex-blue hover:underline"
                  >
                    {humanize(d.document_type)}
                  </a>
                ) : (
                  <span>
                    {humanize(d.document_type)} — unavailable
                    {d.unavailable_reason ? ` (${d.unavailable_reason})` : ""}
                  </span>
                )}{" "}
                <Mono className="text-neutral-500">{d.sha256}</Mono>
                {d.expires_at ? (
                  <span className="text-neutral-500">
                    {" "}
                    · link expires {formatDate(d.expires_at)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-neutral-600">
            These links are shown once and are not stored here. Export again
            (with a reason) if you need fresh ones.
          </p>
        </Alert>
      )}
    </ItemBox>
  );
}

// ── submissions (PR 12) ──

function SubmissionsCard({
  caseId,
  packages,
  submissions,
  submissionsError,
  documents,
  cleanDocuments,
}: {
  caseId: string;
  packages: SubmissionPackageView[];
  submissions: LenderSubmissionView[];
  submissionsError: unknown;
  documents: DocumentView[];
  cleanDocuments: DocumentView[];
}) {
  const [preparing, setPreparing] = useState(false);
  return (
    <Card
      title="Submissions"
      actions={
        packages.length > 0 ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPreparing((v) => !v)}
          >
            {preparing ? "Cancel" : "Prepare submission"}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-neutral-500">
          A submission is one attempt to put one package in front of one
          partner. Dispatching it is the only thing that moves the case to
          lender review. For manual partners nothing is transmitted from here:
          you export the package, send it through the partner's approved
          channel, and record here what you did.
        </p>
        {preparing && (
          <PrepareForm
            caseId={caseId}
            packages={packages}
            submissions={submissions}
            onDone={() => setPreparing(false)}
          />
        )}
        {submissionsError ? <Alert tone="error">{errorMessage(submissionsError)}</Alert> : null}
        {submissions.length === 0 && (
          <Empty>
            No submissions.{" "}
            {packages.length === 0
              ? "Build a package first."
              : "Prepare one from a built package."}
          </Empty>
        )}
        {submissions.map((s) => (
          <SubmissionItem
            key={s.id}
            caseId={caseId}
            submission={s}
            documents={documents}
            cleanDocuments={cleanDocuments}
          />
        ))}
      </div>
    </Card>
  );
}

function PrepareForm({
  caseId,
  packages,
  submissions,
  onDone,
}: {
  caseId: string;
  packages: SubmissionPackageView[];
  submissions: LenderSubmissionView[];
  onDone: () => void;
}) {
  const { key, rotate, rotateIfRefused } = useIdempotencyKey();
  const prepare = usePrepareSubmission(caseId, key);
  const [packageId, setPackageId] = useState(packages[0]?.id ?? "");
  const [supersedes, setSupersedes] = useState("");
  const pkg = packages.find((p) => p.id === packageId);
  // A still-active submission to the same partner must be named as the one this supersedes.
  const supersedable = submissions.filter(
    (s) =>
      pkg &&
      s.partner_id === pkg.partner_id &&
      SENT_SUBMISSION_STATUSES.includes(s.status),
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    prepare.mutate(
      {
        package_id: packageId,
        ...(supersedes ? { supersedes_submission_id: supersedes } : {}),
      },
      {
        onSuccess: () => {
          rotate();
          onDone();
        },
        onError: rotateIfRefused,
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <Panel>
        <Field
          label="Package"
          hint="The submission carries this package's manifest hash; the partner receives exactly what it lists."
        >
          <Select
            value={packageId}
            onChange={(e) => {
              setPackageId(e.target.value);
              setSupersedes("");
            }}
            required
            disabled={prepare.isPending}
          >
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.partner_display_name} · {p.program_code} v{p.program_version}{" "}
                · built {formatDate(p.built_at)} · {p.manifest_sha256.slice(0, 12)}…
              </option>
            ))}
          </Select>
        </Field>
        {supersedable.length > 0 && (
          <Field
            label="Supersedes"
            hint="An earlier submission to this partner is still in front of them. Name it here and it is marked superseded once this one is dispatched; otherwise the server refuses with active_submission_exists."
          >
            <Select
              value={supersedes}
              onChange={(e) => setSupersedes(e.target.value)}
              disabled={prepare.isPending}
            >
              <option value="">Do not supersede</option>
              {supersedable.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.partner_display_name} · {humanize(s.status)} ·{" "}
                  {s.external_reference ?? s.id}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {prepare.isError && <ServerError error={prepare.error} />}
        <div className="flex justify-end">
          <Button type="submit" disabled={prepare.isPending || !packageId}>
            {prepare.isPending ? "Preparing…" : "Prepare"}
          </Button>
        </div>
      </Panel>
    </form>
  );
}

// Mirrors the server's transition table (apps/api/src/services/submissions.ts). The server
// still decides; these only keep buttons for impossible transitions off the screen.
const RECONCILABLE_FROM: Record<ReconcileOutcome, readonly SubmissionStatus[]> = {
  acknowledged: ["dispatched", "delivery_unknown"],
  delivery_unknown: ["prepared", "dispatched", "acknowledged"],
  delivery_failed: ["prepared", "dispatched", "delivery_unknown"],
};
const POLLABLE: readonly SubmissionStatus[] = ["dispatched", "acknowledged", "delivery_unknown"];
const WITHDRAWABLE: readonly SubmissionStatus[] = ["prepared", "dispatched", "acknowledged", "delivery_unknown"];

type SubmissionAction = "dispatch" | "reconcile" | "withdraw" | null;

function SubmissionItem({
  caseId,
  submission: s,
  documents,
  cleanDocuments,
}: {
  caseId: string;
  submission: LenderSubmissionView;
  documents: DocumentView[];
  cleanDocuments: DocumentView[];
}) {
  const [action, setAction] = useState<SubmissionAction>(
    s.status === "prepared" ? "dispatch" : null,
  );
  const poll = usePollSubmission(caseId, s.id);
  const reconcileOutcomes = RECONCILE_OUTCOMES.filter((o) =>
    RECONCILABLE_FROM[o].includes(s.status),
  );
  const canPoll = s.capabilities.automated_status && POLLABLE.includes(s.status);
  const canWithdraw = WITHDRAWABLE.includes(s.status);
  const toggle = (a: Exclude<SubmissionAction, null>) =>
    setAction((cur) => (cur === a ? null : a));

  return (
    <ItemBox>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-neutral-900">
          {s.partner_display_name}
        </span>
        <Badge tone={submissionTone[s.status]}>{humanize(s.status)}</Badge>
        <Badge tone={neutralTone}>{humanize(s.method)}</Badge>
        <SyntheticBadge synthetic={s.synthetic} />
        <span className="ml-auto text-xs text-neutral-500">
          attempt {s.attempt_count} · created {formatDate(s.created_at)}
        </span>
      </div>
      <dl>
        <Row label="Submission id" value={<Mono>{s.id}</Mono>} />
        <Row label="Package" value={<Mono>{s.package_id}</Mono>} />
        <Row
          label="Manifest SHA-256"
          value={<Mono className="break-all text-xs">{s.manifest_sha256}</Mono>}
        />
        <Row
          label="Partner reference"
          value={s.external_reference ? <Mono>{s.external_reference}</Mono> : null}
        />
        <Row label="Dispatched" value={s.dispatched_at ? formatDate(s.dispatched_at) : null} />
        <Row label="Acknowledged" value={s.acknowledged_at ? formatDate(s.acknowledged_at) : null} />
        <Row label="Last attempt" value={s.last_attempt_at ? formatDate(s.last_attempt_at) : null} />
        {s.delivery_evidence_document_id && (
          <Row
            label="Delivery evidence"
            value={<DocumentName id={s.delivery_evidence_document_id} documents={documents} />}
          />
        )}
        {s.failure_reason && <Row label="Failure reason" value={s.failure_reason} />}
        {s.withdrawn_at && (
          <Row
            label="Withdrawn"
            value={`${formatDate(s.withdrawn_at)}${s.withdrawn_reason ? ` — ${s.withdrawn_reason}` : ""}`}
          />
        )}
        {s.supersedes_submission_id && (
          <Row label="Supersedes" value={<Mono>{s.supersedes_submission_id}</Mono>} />
        )}
        {s.superseded_by_submission_id && (
          <Row label="Superseded by" value={<Mono>{s.superseded_by_submission_id}</Mono>} />
        )}
      </dl>
      {s.dispatch_attestation && (
        <div className="rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-2 text-xs">
          <p className="font-medium text-neutral-800">Operator attestation</p>
          <p>
            Sent {formatDate(s.dispatch_attestation.sent_at)} via{" "}
            {CHANNEL_LABELS[s.dispatch_attestation.channel]} to{" "}
            {s.dispatch_attestation.destination}
          </p>
          <p className="whitespace-pre-wrap text-neutral-700">{s.dispatch_attestation.note}</p>
          <p className="text-neutral-500">
            Attested {formatDate(s.dispatch_attestation.attested_at)} by{" "}
            <Mono>{s.dispatch_attestation.attested_by_user_id}</Mono>
          </p>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        {s.status === "prepared" && (
          <Button type="button" variant="secondary" onClick={() => toggle("dispatch")}>
            {action === "dispatch" ? "Hide dispatch" : "Dispatch"}
          </Button>
        )}
        {canPoll && (
          <Button
            type="button"
            variant="secondary"
            disabled={poll.isPending}
            onClick={() => poll.mutate()}
          >
            {poll.isPending ? "Polling…" : "Poll status"}
          </Button>
        )}
        {reconcileOutcomes.length > 0 && (
          <Button type="button" variant="secondary" onClick={() => toggle("reconcile")}>
            {action === "reconcile" ? "Hide reconcile" : "Reconcile"}
          </Button>
        )}
        {canWithdraw && (
          <Button type="button" variant="danger" onClick={() => toggle("withdraw")}>
            {action === "withdraw" ? "Hide withdraw" : "Withdraw"}
          </Button>
        )}
      </div>
      {poll.isError && <ServerError error={poll.error} />}

      {action === "dispatch" && s.status === "prepared" && (
        <DispatchPanel caseId={caseId} submission={s} cleanDocuments={cleanDocuments} />
      )}
      {action === "reconcile" && reconcileOutcomes.length > 0 && (
        <ReconcileForm
          caseId={caseId}
          submission={s}
          outcomes={reconcileOutcomes}
          cleanDocuments={cleanDocuments}
          onDone={() => setAction(null)}
        />
      )}
      {action === "withdraw" && canWithdraw && (
        <WithdrawForm caseId={caseId} submission={s} onDone={() => setAction(null)} />
      )}
    </ItemBox>
  );
}

function DispatchPanel({
  caseId,
  submission: s,
  cleanDocuments,
}: {
  caseId: string;
  submission: LenderSubmissionView;
  cleanDocuments: DocumentView[];
}) {
  const readiness = useSubmissionReadiness(caseId, s.id);
  const { rotateIfRefused, key } = useIdempotencyKey();
  const dispatch = useDispatchSubmission(caseId, s.id, key);
  const manual = s.capabilities.requires_attestation;
  const [sentAt, setSentAt] = useState(nowLocal);
  const [channel, setChannel] = useState<ManualDispatchChannel>("partner_portal");
  const [destination, setDestination] = useState("");
  const [reference, setReference] = useState("");
  const [evidence, setEvidence] = useState("");
  const [note, setNote] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body: DispatchSubmissionBody = manual
      ? {
          attestation: {
            sent_at: localToIso(sentAt),
            channel,
            destination: destination.trim(),
            note: note.trim(),
            ...(reference.trim() ? { external_reference: reference.trim() } : {}),
            ...(evidence ? { evidence_document_id: evidence } : {}),
          },
        }
      : {};
    dispatch.mutate(body, { onError: rotateIfRefused });
  };

  return (
    <form onSubmit={submit}>
      <Panel>
        <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
          Dispatch
        </p>
        {readiness.isPending && <Empty>Checking readiness…</Empty>}
        {readiness.isError && <ServerError error={readiness.error} />}
        {readiness.data && readiness.data.blockers.length > 0 && (
          <>
            <p className="text-sm text-neutral-800">
              The server will refuse this dispatch until these clear:
            </p>
            <BlockerList blockers={readiness.data.blockers} />
          </>
        )}
        {readiness.data && readiness.data.blockers.length === 0 && (
          <Alert tone="success">No blockers. The package is current and the partner is reachable by this method.</Alert>
        )}

        {manual ? (
          <>
            <Alert tone="info">
              Nothing is transmitted from here. Export the package, deliver it
              through the partner's approved channel yourself, then record
              below when and where it went. This attestation is stored
              verbatim and audited; it is what moves the case to lender review.
            </Alert>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Sent at" hint="When you sent it, in your local time.">
                <Input
                  type="datetime-local"
                  value={sentAt}
                  onChange={(e) => setSentAt(e.target.value)}
                  required
                  disabled={dispatch.isPending}
                />
              </Field>
              <Field label="Channel">
                <Select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as ManualDispatchChannel)}
                  disabled={dispatch.isPending}
                >
                  {MANUAL_DISPATCH_CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {CHANNEL_LABELS[c]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field
              label="Destination"
              hint="Portal name, mailbox or courier — never credentials."
            >
              <Input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                required
                minLength={2}
                maxLength={300}
                disabled={dispatch.isPending}
              />
            </Field>
            <Field label="Partner reference (optional)" hint="Confirmation or application number the partner gave you, if any.">
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={200}
                disabled={dispatch.isPending}
              />
            </Field>
            <DocumentPicker
              label="Evidence document (optional)"
              hint="A portal confirmation capture or sent-mail receipt uploaded to this case, if you have one."
              value={evidence}
              onChange={setEvidence}
              documents={cleanDocuments}
              disabled={dispatch.isPending}
            />
            <Field label="Note" hint="What you sent and how; at least a few words.">
              <Textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                required
                minLength={3}
                maxLength={2000}
                disabled={dispatch.isPending}
              />
            </Field>
          </>
        ) : (
          <Alert tone="info">
            {s.method === "mock"
              ? "Synthetic adapter: no partner is contacted. The mock accepts the package and hands back a MOCK- reference so the flow can be rehearsed end to end."
              : "This adapter dispatches the package itself; no attestation is needed."}
          </Alert>
        )}

        {dispatch.isError && <ServerError error={dispatch.error} />}
        <div className="flex justify-end">
          <Button type="submit" disabled={dispatch.isPending || readiness.isPending}>
            {dispatch.isPending
              ? "Dispatching…"
              : manual
                ? "Record dispatch"
                : `Dispatch (${humanize(s.method)})`}
          </Button>
        </div>
      </Panel>
    </form>
  );
}

function ReconcileForm({
  caseId,
  submission: s,
  outcomes,
  cleanDocuments,
  onDone,
}: {
  caseId: string;
  submission: LenderSubmissionView;
  outcomes: ReconcileOutcome[];
  cleanDocuments: DocumentView[];
  onDone: () => void;
}) {
  const reconcile = useReconcileSubmission(caseId, s.id);
  const [outcome, setOutcome] = useState<ReconcileOutcome>(outcomes[0] ?? "acknowledged");
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  const [evidence, setEvidence] = useState("");
  const [occurredAt, setOccurredAt] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    reconcile.mutate(
      {
        outcome,
        note: note.trim(),
        ...(reference.trim() ? { external_reference: reference.trim() } : {}),
        ...(evidence ? { evidence_document_id: evidence } : {}),
        ...(occurredAt ? { occurred_at: localToIso(occurredAt) } : {}),
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={submit}>
      <Panel>
        <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
          Reconcile
        </p>
        <p className="text-xs text-neutral-600">
          Record what the partner told you about delivery. Only transitions the
          server allows from <Mono>{s.status}</Mono> are offered.
        </p>
        <Field label="Outcome">
          <Select
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as ReconcileOutcome)}
            disabled={reconcile.isPending}
          >
            {outcomes.map((o) => (
              <option key={o} value={o}>
                {RECONCILE_LABELS[o]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Note" hint="Who said what, and how it reached you.">
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
            minLength={3}
            maxLength={2000}
            disabled={reconcile.isPending}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Partner reference (optional)">
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={200}
              disabled={reconcile.isPending}
            />
          </Field>
          <Field label="Occurred at (optional)" hint="Partner-stated time; blank records now.">
            <Input
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              disabled={reconcile.isPending}
            />
          </Field>
        </div>
        <DocumentPicker
          label="Evidence document (optional)"
          value={evidence}
          onChange={setEvidence}
          documents={cleanDocuments}
          disabled={reconcile.isPending}
        />
        {reconcile.isError && <ServerError error={reconcile.error} />}
        <div className="flex justify-end">
          <Button type="submit" disabled={reconcile.isPending}>
            {reconcile.isPending ? "Saving…" : "Record reconciliation"}
          </Button>
        </div>
      </Panel>
    </form>
  );
}

function WithdrawForm({
  caseId,
  submission: s,
  onDone,
}: {
  caseId: string;
  submission: LenderSubmissionView;
  onDone: () => void;
}) {
  const withdraw = useWithdrawSubmission(caseId, s.id);
  const [reason, setReason] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    withdraw.mutate({ reason: reason.trim() }, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit}>
      <Panel>
        <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
          Withdraw
        </p>
        <Field
          label="Reason"
          hint="Withdrawing records that Tex pulled this attempt. It does not tell the partner; do that through their channel."
        >
          <Textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={3}
            maxLength={2000}
            disabled={withdraw.isPending}
          />
        </Field>
        {withdraw.isError && <ServerError error={withdraw.error} />}
        <div className="flex justify-end">
          <Button type="submit" variant="danger" disabled={withdraw.isPending}>
            {withdraw.isPending ? "Withdrawing…" : "Withdraw submission"}
          </Button>
        </div>
      </Panel>
    </form>
  );
}

// ── decisions (PR 13) ──

function DecisionsCard({
  caseId,
  view,
  submissions,
  decisions,
  decisionsError,
  documents,
  cleanDocuments,
}: {
  caseId: string;
  view: TexCaseView;
  submissions: LenderSubmissionView[];
  decisions: LenderDecisionView[] | undefined;
  decisionsError: unknown;
  documents: DocumentView[];
  cleanDocuments: DocumentView[];
}) {
  const relevant = submissions.filter(
    (s) =>
      SENT_SUBMISSION_STATUSES.includes(s.status) ||
      decisionsForSubmission(decisions, s.id).length > 0,
  );
  return (
    <Card title="Decisions">
      <div className="space-y-4">
        <p className="text-xs text-neutral-500">
          The partner's answer to each submission, recorded verbatim and
          append-only. A change of mind (counteroffer, expiry, withdrawal) is a
          new decision on the same submission; the earlier one is then shown as
          superseded.
        </p>
        {decisionsError ? <Alert tone="error">{errorMessage(decisionsError)}</Alert> : null}
        {relevant.length === 0 && (
          <Empty>No submission has been sent to a partner yet, so there is nothing to decide on.</Empty>
        )}
        {relevant.map((s) => (
          <SubmissionDecisions
            key={s.id}
            caseId={caseId}
            view={view}
            submission={s}
            decisions={decisionsForSubmission(decisions, s.id)}
            documents={documents}
            cleanDocuments={cleanDocuments}
          />
        ))}
      </div>
    </Card>
  );
}

// Mirrors apps/api/src/services/decisions.ts; the server still decides.
const DECIDABLE_STAGES: readonly TexCaseView["stage"][] = ["lender_review", "offer_review", "closing"];

function SubmissionDecisions({
  caseId,
  view,
  submission: s,
  decisions,
  documents,
  cleanDocuments,
}: {
  caseId: string;
  view: TexCaseView;
  submission: LenderSubmissionView;
  decisions: LenderDecisionView[];
  documents: DocumentView[];
  cleanDocuments: DocumentView[];
}) {
  const [recording, setRecording] = useState(false);
  const canRecord =
    SENT_SUBMISSION_STATUSES.includes(s.status) && DECIDABLE_STAGES.includes(view.stage);
  const ordered = [...decisions].sort((a, b) => b.recorded_at.localeCompare(a.recorded_at));
  return (
    <ItemBox>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-neutral-900">{s.partner_display_name}</span>
        <Badge tone={submissionTone[s.status]}>{humanize(s.status)}</Badge>
        <SyntheticBadge synthetic={s.synthetic} />
        <span className="text-xs text-neutral-500">
          submission <Mono>{s.external_reference ?? s.id}</Mono>
        </span>
        {canRecord && (
          <Button
            type="button"
            variant="secondary"
            className="ml-auto"
            onClick={() => setRecording((v) => !v)}
          >
            {recording ? "Cancel" : "Record decision"}
          </Button>
        )}
      </div>
      {recording && canRecord && (
        <RecordDecisionForm
          caseId={caseId}
          submission={s}
          cleanDocuments={cleanDocuments}
          onDone={() => setRecording(false)}
        />
      )}
      {ordered.length === 0 && <Empty>No decision recorded yet.</Empty>}
      {ordered.map((d) => (
        <DecisionItem key={d.id} decision={d} documents={documents} />
      ))}
    </ItemBox>
  );
}

function DecisionItem({
  decision: d,
  documents,
}: {
  decision: LenderDecisionView;
  documents: DocumentView[];
}) {
  return (
    <div className="space-y-1 rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={outcomeTone[d.outcome]}>{humanize(d.outcome)}</Badge>
        {d.current ? (
          <Badge tone={neutralTone}>current</Badge>
        ) : (
          <Badge tone={neutralTone}>superseded</Badge>
        )}
        <SyntheticBadge synthetic={d.synthetic} />
        {d.external_reference && (
          <span className="text-xs text-neutral-500">
            ref <Mono>{d.external_reference}</Mono>
          </span>
        )}
      </div>
      <dl>
        <Row
          label="Partner decided"
          value={d.decided_at ? formatDate(d.decided_at) : "not stated by partner"}
        />
        <Row label="Recorded by Tex" value={formatDate(d.recorded_at)} />
        {d.provider_event_id && (
          <Row label="Provider event" value={<Mono>{d.provider_event_id}</Mono>} />
        )}
        <Row
          label="Evidence"
          value={
            d.evidence_document_id ? (
              <DocumentName id={d.evidence_document_id} documents={documents} />
            ) : null
          }
        />
        {d.offer_id && <Row label="Offer" value={<Mono>{d.offer_id}</Mono>} />}
      </dl>
      {d.partner_reason_text && (
        <div className="text-sm">
          <p className="text-xs text-neutral-500">Partner's wording</p>
          <p className="whitespace-pre-wrap text-neutral-800">{d.partner_reason_text}</p>
        </div>
      )}
      {d.conditions.length > 0 && (
        <StringList title="Conditions" items={d.conditions} />
      )}
      {d.requested_information.length > 0 && (
        <StringList title="Information requested" items={d.requested_information} />
      )}
    </div>
  );
}

function StringList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="text-sm">
      <p className="text-xs text-neutral-500">{title}</p>
      <ul className="list-disc pl-5 text-neutral-800">
        {items.map((c, i) => (
          <li key={`${i}-${c}`}>{c}</li>
        ))}
      </ul>
    </div>
  );
}

type RateMode = "none" | "bps" | "text";

function RecordDecisionForm({
  caseId,
  submission: s,
  cleanDocuments,
  onDone,
}: {
  caseId: string;
  submission: LenderSubmissionView;
  cleanDocuments: DocumentView[];
  onDone: () => void;
}) {
  const { key, rotateIfRefused } = useIdempotencyKey();
  const record = useRecordDecision(caseId, s.id, key);
  const [formError, setFormError] = useState<string | null>(null);

  const [outcome, setOutcome] = useState<RecordableDecisionOutcome>("approved_offer");
  const [reasonText, setReasonText] = useState("");
  const [conditions, setConditions] = useState("");
  const [requested, setRequested] = useState("");
  const [evidence, setEvidence] = useState("");
  const [reference, setReference] = useState("");
  const [decidedAt, setDecidedAt] = useState("");

  const [productType, setProductType] = useState("");
  const [approvedAmount, setApprovedAmount] = useState("");
  const [downPayment, setDownPayment] = useState("");
  const [termMonths, setTermMonths] = useState("");
  const [frequency, setFrequency] = useState<PaymentFrequency>("monthly");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [rateMode, setRateMode] = useState<RateMode>("none");
  const [rateBps, setRateBps] = useState("");
  const [rateType, setRateType] = useState("");
  const [rateText, setRateText] = useState("");
  const [fees, setFees] = useState("");
  const [residual, setResidual] = useState("");
  const [totalFinanceCharge, setTotalFinanceCharge] = useState("");
  const [guarantees, setGuarantees] = useState("");
  const [prepayment, setPrepayment] = useState("");
  const [buyout, setBuyout] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [termsDocument, setTermsDocument] = useState("");
  const [disclosureStatus, setDisclosureStatus] = useState<DisclosureStatus>("pending");
  const [disclosureNote, setDisclosureNote] = useState("");
  const [disclosureDocument, setDisclosureDocument] = useState("");

  const offerBearing = isOfferOutcome(outcome);

  const buildDisclosure = (): DisclosureInput => {
    switch (disclosureStatus) {
      case "pending":
        return { status: "pending" };
      case "not_required":
        return { status: "not_required", note: disclosureNote.trim() };
      case "provider_delivered":
        return { status: "provider_delivered", document_id: disclosureDocument };
    }
  };

  const buildOffer = (conditionList: string[]): OfferTermsInput => {
    const approved = moneyInput(approvedAmount, "Approved amount");
    if (!approved) throw new Error("Approved amount: enter the amount the partner approved");
    const term = integerInput(termMonths, "Term (months)");
    if (term === undefined) throw new Error("Term (months): enter the term the partner stated");
    const bps = rateMode === "bps" ? integerInput(rateBps, "Rate (basis points)") : undefined;
    if (rateMode === "bps" && bps === undefined)
      throw new Error("Rate (basis points): enter the rate in basis points, or switch to the partner's wording");
    const text = rateMode === "text" ? trimmedOrUndefined(rateText) : undefined;
    if (rateMode === "text" && text === undefined)
      throw new Error("Rate wording: paste the partner's own words, or choose 'not stated'");
    // Every optional term is omitted when blank so the server lists it under `unstated_terms`.
    const offer: OfferTermsInput = {
      product_type: productType.trim(),
      approved_amount: approved,
      term_months: term,
      payment_frequency: frequency,
      disclosure: buildDisclosure(),
    };
    const down = moneyInput(downPayment, "Down payment");
    if (down) offer.down_payment = down;
    const payment = moneyInput(paymentAmount, "Payment amount");
    if (payment) offer.payment_amount = payment;
    if (bps !== undefined) offer.rate_bps = bps;
    if (rateMode !== "none" && rateType.trim()) offer.rate_type = rateType.trim();
    if (text !== undefined) offer.rate_text = text;
    const fee = moneyInput(fees, "Fees");
    if (fee) offer.fees = fee;
    const res = moneyInput(residual, "Residual");
    if (res) offer.residual = res;
    const tfc = moneyInput(totalFinanceCharge, "Total finance charge");
    if (tfc) offer.total_finance_charge = tfc;
    const guaranteeList = nonEmptyOrUndefined(
      boundedLines(guarantees, "Guarantee requirements"),
    );
    if (guaranteeList) offer.guarantee_requirements = guaranteeList;
    if (prepayment.trim()) offer.prepayment_terms = prepayment.trim();
    if (buyout.trim()) offer.buyout_terms = buyout.trim();
    if (conditionList.length) offer.conditions = conditionList;
    if (expiresAt) offer.expires_at = expiresAt;
    if (termsDocument) offer.terms_document_id = termsDocument;
    return offer;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    let body: RecordDecisionBody;
    try {
      const conditionList = boundedLines(conditions, "Conditions");
      const requestedList = boundedLines(requested, "Requested information");
      body = {
        outcome,
        ...(trimmedOrUndefined(reasonText) ? { partner_reason_text: reasonText.trim() } : {}),
        ...(conditionList.length ? { conditions: conditionList } : {}),
        ...(requestedList.length ? { requested_information: requestedList } : {}),
        ...(evidence ? { evidence_document_id: evidence } : {}),
        ...(trimmedOrUndefined(reference) ? { external_reference: reference.trim() } : {}),
        ...(decidedAt ? { decided_at: localToIso(decidedAt) } : {}),
        ...(offerBearing ? { offer: buildOffer(conditionList) } : {}),
      };
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Check the form");
      return;
    }
    record.mutate(body, { onSuccess: onDone, onError: rotateIfRefused });
  };

  const disabled = record.isPending;

  return (
    <form onSubmit={submit}>
      <Panel>
        <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
          Record the partner's decision
        </p>
        <p className="text-xs text-neutral-600">
          Transcribe the partner's letter or portal screen as written. Leave
          anything the partner did not state blank — it is shown as "not
          stated", never as zero or a guess. Nothing here is calculated from
          what you type.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Outcome">
            <Select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as RecordableDecisionOutcome)}
              disabled={disabled}
            >
              {RECORDABLE_DECISION_OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {OUTCOME_LABELS[o]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Partner decided at (optional)" hint="The time on the partner's letter; Tex's recording time is stored separately.">
            <Input
              type="datetime-local"
              value={decidedAt}
              onChange={(e) => setDecidedAt(e.target.value)}
              disabled={disabled}
            />
          </Field>
        </div>
        <Field label="Partner's wording (optional)" hint="Their reason or decision text, verbatim.">
          <Textarea
            rows={3}
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            maxLength={5000}
            disabled={disabled}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Conditions (one per line)" hint={offerBearing ? "Stored on the decision and on the offer's terms." : undefined}>
            <Textarea
              rows={4}
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              disabled={disabled}
            />
          </Field>
          <Field label="Information requested (one per line)">
            <Textarea
              rows={4}
              value={requested}
              onChange={(e) => setRequested(e.target.value)}
              disabled={disabled}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <DocumentPicker
            label={s.synthetic ? "Evidence document (optional for a synthetic partner)" : "Evidence document"}
            hint="The partner's letter or portal capture. The server requires it for a live partner."
            value={evidence}
            onChange={setEvidence}
            documents={cleanDocuments}
            required={!s.synthetic}
            disabled={disabled}
          />
          <Field label="Partner reference (optional)">
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              maxLength={200}
              disabled={disabled}
            />
          </Field>
        </div>

        {offerBearing && (
          <div className="space-y-3 border-t border-neutral-200 pt-3">
            <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--text-strong)]">
              Offer terms, as stated by the partner
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Product type" hint="The partner's own product name: equipment loan, EFA, FMV lease, $1 buyout lease…">
                <Input value={productType} onChange={(e) => setProductType(e.target.value)} required maxLength={500} disabled={disabled} />
              </Field>
              <Field label="Approved amount (USD)" hint="Dollars and cents as written, e.g. 135850.00.">
                <Input value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} required inputMode="decimal" disabled={disabled} />
              </Field>
              <Field label="Term (months)">
                <Input value={termMonths} onChange={(e) => setTermMonths(e.target.value)} required inputMode="numeric" disabled={disabled} />
              </Field>
              <Field label="Payment frequency">
                <Select value={frequency} onChange={(e) => setFrequency(e.target.value as PaymentFrequency)} disabled={disabled}>
                  {PAYMENT_FREQUENCIES.map((f) => (
                    <option key={f} value={f}>
                      {FREQUENCY_LABELS[f]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Down payment (USD, optional)">
                <Input value={downPayment} onChange={(e) => setDownPayment(e.target.value)} inputMode="decimal" disabled={disabled} />
              </Field>
              <Field label="Payment amount (USD, optional)" hint="Only if the partner stated it. Not computed from the amount and term.">
                <Input value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} inputMode="decimal" disabled={disabled} />
              </Field>
            </div>

            <Field label="Rate" hint="Either a rate the partner stated as a rate, or their wording (a factor, prime + x). One is never converted into the other.">
              <Select value={rateMode} onChange={(e) => setRateMode(e.target.value as RateMode)} disabled={disabled}>
                <option value="none">Not stated by partner</option>
                <option value="bps">Stated as a rate (basis points)</option>
                <option value="text">Stated in other words (verbatim)</option>
              </Select>
            </Field>
            {rateMode !== "none" && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {rateMode === "bps" ? (
                  <Field label="Rate (basis points)" hint="9.49% is 949. Type the number the partner stated; do not derive it from a payment.">
                    <Input value={rateBps} onChange={(e) => setRateBps(e.target.value)} required inputMode="numeric" disabled={disabled} />
                  </Field>
                ) : (
                  <Field label="Rate wording (verbatim)">
                    <Input value={rateText} onChange={(e) => setRateText(e.target.value)} required maxLength={500} disabled={disabled} />
                  </Field>
                )}
                <Field label="Rate type (optional)" hint="fixed, variable, factor…">
                  <Input value={rateType} onChange={(e) => setRateType(e.target.value)} maxLength={500} disabled={disabled} />
                </Field>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field label="Fees (USD, optional)">
                <Input value={fees} onChange={(e) => setFees(e.target.value)} inputMode="decimal" disabled={disabled} />
              </Field>
              <Field label="Residual / buyout amount (USD, optional)">
                <Input value={residual} onChange={(e) => setResidual(e.target.value)} inputMode="decimal" disabled={disabled} />
              </Field>
              <Field label="Total finance charge (USD, optional)">
                <Input value={totalFinanceCharge} onChange={(e) => setTotalFinanceCharge(e.target.value)} inputMode="decimal" disabled={disabled} />
              </Field>
            </div>
            <Field label="Guarantee requirements (one per line, optional)">
              <Textarea rows={2} value={guarantees} onChange={(e) => setGuarantees(e.target.value)} disabled={disabled} />
            </Field>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Prepayment terms (optional)">
                <Input value={prepayment} onChange={(e) => setPrepayment(e.target.value)} maxLength={500} disabled={disabled} />
              </Field>
              <Field label="Buyout terms (optional)">
                <Input value={buyout} onChange={(e) => setBuyout(e.target.value)} maxLength={500} disabled={disabled} />
              </Field>
              <Field label="Offer expires on (optional)" hint="The date in the letter. The server marks the offer expired after it.">
                <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} disabled={disabled} />
              </Field>
              <DocumentPicker
                label="Terms document (optional)"
                hint="The partner's term sheet, if separate from the evidence document."
                value={termsDocument}
                onChange={setTermsDocument}
                documents={cleanDocuments}
                disabled={disabled}
              />
            </div>

            <div className="space-y-2 border-t border-neutral-200 pt-3">
              <Field
                label="Provider disclosure"
                hint="State law may require the provider to give the buyer a disclosure before they can consider this offer. While it is pending in a covered state the buyer cannot see or select the offer. This can be updated later from the Offers card."
              >
                <Select value={disclosureStatus} onChange={(e) => setDisclosureStatus(e.target.value as DisclosureStatus)} disabled={disabled}>
                  {DISCLOSURE_STATUSES.map((d) => (
                    <option key={d} value={d}>
                      {DISCLOSURE_LABELS[d]}
                    </option>
                  ))}
                </Select>
              </Field>
              {disclosureStatus === "not_required" && (
                <Field label="Why no disclosure is required" hint="Your basis, e.g. what the partner's letter says about the product and the state rule you checked.">
                  <Textarea rows={2} value={disclosureNote} onChange={(e) => setDisclosureNote(e.target.value)} required minLength={3} maxLength={2000} disabled={disabled} />
                </Field>
              )}
              {disclosureStatus === "provider_delivered" && (
                <DocumentPicker
                  label="Disclosure document"
                  hint="The disclosure the provider delivered, uploaded to this case."
                  value={disclosureDocument}
                  onChange={setDisclosureDocument}
                  documents={cleanDocuments}
                  required
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        )}

        {formError && <Alert tone="error">{formError}</Alert>}
        {record.isError && <ServerError error={record.error} />}
        <div className="flex justify-end">
          <Button type="submit" disabled={disabled}>
            {record.isPending ? "Recording…" : "Record decision"}
          </Button>
        </div>
      </Panel>
    </form>
  );
}

// ── offers (PR 13) ──

function OffersCard({
  caseId,
  offers,
  offersError,
  documents,
  cleanDocuments,
}: {
  caseId: string;
  offers: ReturnType<typeof useOffers>["data"];
  offersError: unknown;
  documents: DocumentView[];
  cleanDocuments: DocumentView[];
}) {
  return (
    <Card title="Offers">
      <div className="space-y-4">
        {offersError ? <Alert tone="error">{errorMessage(offersError)}</Alert> : null}
        {offers && (
          <dl>
            <Row label="Availability" value={`${humanize(offers.availability.availability)} — ${offers.availability.message}`} />
            <Row label="Submissions awaiting a partner" value={offers.availability.submissions_awaiting} />
            <Row label="Active offers" value={offers.availability.offers_active} />
            <Row
              label="Buyer's selection"
              value={
                offers.selected_offer_id ? (
                  <Mono>{offers.selected_offer_id}</Mono>
                ) : (
                  "none — the buyer selects from their own view; Tex records it elsewhere"
                )
              }
            />
          </dl>
        )}
        {offers && offers.offers.length === 0 && <Empty>No offer has been recorded on this case.</Empty>}
        {offers?.offers.map((o) => (
          <OfferItem key={o.id} caseId={caseId} offer={o} documents={documents} cleanDocuments={cleanDocuments} />
        ))}
      </div>
    </Card>
  );
}

function money(m: MoneyView | null): ReactNode {
  return m ? <Mono>{formatAmount(m)}</Mono> : null;
}

function OfferItem({
  caseId,
  offer: o,
  documents,
  cleanDocuments,
}: {
  caseId: string;
  offer: OfferView;
  documents: DocumentView[];
  cleanDocuments: DocumentView[];
}) {
  const [recording, setRecording] = useState(false);
  const t = o.terms;
  const unstated = OFFER_OPTIONAL_TERMS.filter((k) => t.unstated_terms.includes(k));
  const withheld = !o.disclosure.presentable_to_buyer;

  return (
    <ItemBox>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-neutral-900">{o.partner_display_name}</span>
        <span className="text-sm text-neutral-700">· {t.product_type}</span>
        <Badge tone={offerTone[o.status]}>{o.status}</Badge>
        <Badge tone={outcomeTone[o.outcome]}>{humanize(o.outcome)}</Badge>
        {o.selected && <Badge tone={greenTone}>selected by buyer</Badge>}
        {withheld && <Badge tone={amberTone}>withheld from buyer</Badge>}
        <SyntheticBadge synthetic={o.synthetic} />
        <span className="ml-auto text-xs text-neutral-500">recorded {formatDate(o.recorded_at)}</span>
      </div>

      <dl>
        <Row label="Offer id" value={<Mono>{o.id}</Mono>} />
        <Row label="Approved amount" value={money(t.approved_amount)} />
        <Row label="Down payment" value={money(t.down_payment)} />
        <Row label="Term" value={`${t.term_months} months`} />
        <Row label="Payment frequency" value={FREQUENCY_LABELS[t.payment_frequency]} />
        <Row label="Payment amount" value={money(t.payment_amount)} />
        <Row
          label="Rate (as stated)"
          value={
            t.rate_bps !== null ? (
              <span>
                <Mono>{t.rate_bps} bps</Mono>
                {t.rate_type ? ` ${t.rate_type}` : ""}
              </span>
            ) : null
          }
        />
        <Row label="Rate wording (verbatim)" value={t.rate_text} />
        <Row label="Fees" value={money(t.fees)} />
        <Row label="Residual / buyout amount" value={money(t.residual)} />
        <Row label="Total finance charge" value={money(t.total_finance_charge)} />
        <Row label="Prepayment terms" value={t.prepayment_terms} />
        <Row label="Buyout terms" value={t.buyout_terms} />
        <Row label="Expires" value={t.expires_at ? formatDay(t.expires_at) : null} />
        <Row
          label="Terms document"
          value={o.terms_document_id ? <DocumentName id={o.terms_document_id} documents={documents} /> : null}
        />
      </dl>
      {t.guarantee_requirements.length > 0 && (
        <StringList title="Guarantee requirements" items={t.guarantee_requirements} />
      )}
      {t.conditions.length > 0 && <StringList title="Conditions" items={t.conditions} />}
      {unstated.length > 0 && (
        <p className="text-sm text-neutral-700">
          <span className="font-medium">Not stated by partner:</span>{" "}
          {unstated.map((k) => OPTIONAL_TERM_LABELS[k]).join(", ")}.
        </p>
      )}

      <div className="rounded-md border border-[var(--border-subtle)] bg-neutral-50 p-2 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-medium text-neutral-800">
            Provider disclosure: {humanize(o.disclosure.status)}
            {o.disclosure.covered_state ? ` · covered state ${o.disclosure.covered_state}` : " · no covered state"}
          </p>
          <Button type="button" variant="secondary" onClick={() => setRecording((v) => !v)}>
            {recording ? "Cancel" : "Record disclosure"}
          </Button>
        </div>
        <p className="text-xs text-neutral-600">
          Recorded {formatDate(o.disclosure.recorded_at)}
          {o.disclosure.document_id ? (
            <>
              {" "}· <DocumentName id={o.disclosure.document_id} documents={documents} />
            </>
          ) : null}
        </p>
        {withheld ? (
          <p className="mt-1 text-xs text-amber-800">
            The buyer cannot see or select this offer until the provider's
            disclosure is recorded as delivered or as not required.
            {o.disclosure.covered_state ? ` ${o.disclosure.covered_state} has a commercial-financing disclosure law that applies here.` : ""}
          </p>
        ) : (
          <p className="mt-1 text-xs text-neutral-600">Presentable to the buyer.</p>
        )}
        {recording && (
          <DisclosureForm
            caseId={caseId}
            offer={o}
            cleanDocuments={cleanDocuments}
            onDone={() => setRecording(false)}
          />
        )}
      </div>
    </ItemBox>
  );
}

function DisclosureForm({
  caseId,
  offer: o,
  cleanDocuments,
  onDone,
}: {
  caseId: string;
  offer: OfferView;
  cleanDocuments: DocumentView[];
  onDone: () => void;
}) {
  const record = useRecordDisclosure(caseId, o.id);
  const [status, setStatus] = useState<DisclosureStatus>(
    o.disclosure.status === "pending" ? "provider_delivered" : o.disclosure.status,
  );
  const [note, setNote] = useState("");
  const [documentId, setDocumentId] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const disclosure: DisclosureInput =
      status === "pending"
        ? { status: "pending" }
        : status === "not_required"
          ? { status: "not_required", note: note.trim() }
          : { status: "provider_delivered", document_id: documentId };
    record.mutate({ disclosure }, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="mt-2 space-y-2">
      <Field
        label="Disclosure status"
        hint="Append-only: each entry is a new record; the latest one governs what the buyer can see."
      >
        <Select value={status} onChange={(e) => setStatus(e.target.value as DisclosureStatus)} disabled={record.isPending}>
          {DISCLOSURE_STATUSES.map((d) => (
            <option key={d} value={d}>
              {DISCLOSURE_LABELS[d]}
            </option>
          ))}
        </Select>
      </Field>
      {status === "not_required" && (
        <Field label="Why no disclosure is required" hint="Your basis — the product, the state rule you checked, who confirmed it.">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required minLength={3} maxLength={2000} disabled={record.isPending} />
        </Field>
      )}
      {status === "provider_delivered" && (
        <DocumentPicker
          label="Disclosure document"
          hint="The disclosure the provider delivered to the buyer, uploaded to this case and scanned clean."
          value={documentId}
          onChange={setDocumentId}
          documents={cleanDocuments}
          required
          disabled={record.isPending}
        />
      )}
      {record.isError && <ServerError error={record.error} />}
      <div className="flex justify-end">
        <Button type="submit" disabled={record.isPending}>
          {record.isPending ? "Recording…" : "Record disclosure"}
        </Button>
      </div>
    </form>
  );
}
